import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getFreemiumStatus, FreemiumFeatureType } from '../../services/tokenService';
import './TrialCountdownWidget.css';

/**
 * Feature surface for the trial widget — three "have you tried it yet"
 * chips. Mapped to FreemiumFeatureType so we can derive "used today" from
 * GET /tokens/freemium-status without adding a dedicated endpoint.
 *
 * Order matters: we render `used` first as celebratory chips, then `unused`
 * as nudge-to-try CTAs.
 */
// Label keys are cross-referenced into the `pricing` namespace where the
// nine-locale `freemium.feature.*` translations already live — no point
// duplicating them under `nav`.
const TRACKED_FEATURES: Array<{
  type: FreemiumFeatureType;
  ctaPath: string;
  labelKey: string;
}> = [
  { type: 'shadowing',       ctaPath: '/learning',         labelKey: 'pricing:freemium.feature.shadowing' },
  { type: 'dictation',       ctaPath: '/learning',         labelKey: 'pricing:freemium.feature.dictation' },
  { type: 'quiz',            ctaPath: '/learning',         labelKey: 'pricing:freemium.feature.quiz' },
  { type: 'vocab_flashcard', ctaPath: '/vocabulary-book',  labelKey: 'pricing:freemium.feature.vocabFlashcard' },
  { type: 'ai_question',     ctaPath: '/learning',         labelKey: 'pricing:freemium.feature.aiQuestion' },
];

const TrialCountdownWidget: React.FC = () => {
  const { t } = useTranslation('nav');
  const { user } = useAuth();
  const [usedSet, setUsedSet] = useState<Set<FreemiumFeatureType> | null>(null);

  useEffect(() => {
    if (!user || user.subscriptionStatus !== 'trialing') return;
    let cancelled = false;
    getFreemiumStatus()
      .then((status) => {
        if (cancelled) return;
        const used = new Set<FreemiumFeatureType>();
        // Subscriber/trialing path returns features={} — we infer "used" from
        // the (still recorded) transaction history. For trialing users the
        // server short-circuits and skips the per-feature query, so we'd see
        // an empty map here. Fall back to a permissive "haven't tracked yet"
        // state by treating everything as unused — the widget still renders.
        Object.entries(status.features ?? {}).forEach(([type, feat]) => {
          if (feat && feat.usedToday > 0) {
            used.add(type as FreemiumFeatureType);
          }
        });
        setUsedSet(used);
      })
      .catch(() => {
        if (!cancelled) setUsedSet(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || user.subscriptionStatus !== 'trialing' || !user.trialEndsAt) {
    return null;
  }

  const trialEndsAt = new Date(user.trialEndsAt).getTime();
  const daysLeft = Math.max(0, Math.ceil((trialEndsAt - Date.now()) / (1000 * 60 * 60 * 24)));
  // Progress = elapsed / total. Total trial is 7 days; if Loopy ever ships
  // longer trials this would need to read TRIAL_CONFIG from the backend.
  const totalDays = 7;
  const elapsedDays = Math.min(totalDays, totalDays - daysLeft);
  const progressPct = Math.round((elapsedDays / totalDays) * 100);

  const usedFeatures = usedSet ? TRACKED_FEATURES.filter((f) => usedSet.has(f.type)) : [];
  const unusedFeatures = usedSet ? TRACKED_FEATURES.filter((f) => !usedSet.has(f.type)) : TRACKED_FEATURES;

  return (
    <div className="trial-widget">
      <div className="trial-widget__head">
        <span className="trial-widget__title">{t('trialWidget.title')}</span>
        <span className="trial-widget__days">
          {t('trialWidget.daysLeft', { count: daysLeft })}
        </span>
      </div>

      <div className="trial-widget__bar-bg">
        <div className="trial-widget__bar-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {usedFeatures.length > 0 && (
        <div className="trial-widget__section">
          <div className="trial-widget__section-label">{t('trialWidget.tried')}</div>
          <div className="trial-widget__chips">
            {usedFeatures.map((f) => (
              <span key={f.type} className="trial-widget__chip trial-widget__chip--used">
                {t(f.labelKey)}
              </span>
            ))}
          </div>
        </div>
      )}

      {unusedFeatures.length > 0 && (
        <div className="trial-widget__section">
          <div className="trial-widget__section-label">{t('trialWidget.notTried')}</div>
          <div className="trial-widget__chips">
            {unusedFeatures.map((f) => (
              <Link key={f.type} to={f.ctaPath} className="trial-widget__chip trial-widget__chip--cta">
                {t(f.labelKey)} →
              </Link>
            ))}
          </div>
        </div>
      )}

      <Link to="/pricing" className="trial-widget__upgrade">
        {t('trialWidget.upgradeCta')}
      </Link>
    </div>
  );
};

export default TrialCountdownWidget;
